/**
 * CsvViewer Component
 *
 * Displays CSV files using TanStack Table with:
 * - Column sorting (click header to sort asc/desc)
 * - Per-column text filtering
 * - Auto-detection of delimiter (comma, tab, pipe, semicolon)
 * - Pagination for large files
 * - Row count and column count display
 */

import { useMemo, useState, useCallback } from 'react';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
  type ColumnFiltersState,
} from '@tanstack/react-table';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import {
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Filter,
  X,
  FileSpreadsheet,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';

// ============================================
// Types
// ============================================

interface CsvViewerProps {
  /** The CSV content to display */
  content: string;
  /** File name (used for display) */
  filename: string;
  /** Additional CSS classes */
  className?: string;
}

type CsvRow = Record<string, string>;

// Maximum rows to parse/display to prevent browser crashes
const MAX_ROWS = 50000;
const PAGE_SIZE_OPTIONS = [25, 50, 100, 250, 500];

// ============================================
// CSV Parsing
// ============================================

/**
 * Detect the delimiter used in CSV content
 */
function detectDelimiter(content: string): string {
  const firstLine = content.split('\n')[0] || '';

  // Count occurrences of common delimiters
  const delimiters = [',', '\t', '|', ';'];
  const counts = delimiters.map((d) => ({
    delimiter: d,
    count: firstLine.split(d).length - 1,
  }));

  // Sort by count descending and pick the most common
  counts.sort((a, b) => b.count - a.count);

  // Return the most common delimiter, default to comma
  return counts[0]?.count > 0 ? counts[0].delimiter : ',';
}

/**
 * Sanitize header name for use as column ID
 * Removes/replaces characters that could cause issues with TanStack Table
 */
function sanitizeColumnId(header: string, index: number): string {
  if (!header.trim()) {
    return `column_${index + 1}`;
  }
  // Replace problematic characters with underscores
  return header.replace(/[.[\]]/g, '_');
}

/**
 * Parse CSV content into an array of row objects
 * Limits to MAX_ROWS to prevent browser crashes on large files
 */
function parseCsv(content: string, delimiter: string): { headers: string[]; columnIds: string[]; rows: CsvRow[]; totalRows: number; truncated: boolean } {
  if (!content.trim()) {
    return { headers: [], columnIds: [], rows: [], totalRows: 0, truncated: false };
  }

  const lines = content.split('\n');

  // Parse first line as headers
  const headerLine = lines[0] || '';
  const rawHeaders = parseCSVLine(headerLine, delimiter);

  // Create display headers and sanitized column IDs
  const headers = rawHeaders.map((h, i) => h.trim() || `Column ${i + 1}`);
  const columnIds = rawHeaders.map((h, i) => sanitizeColumnId(h.trim(), i));

  // Count total non-empty data rows
  let totalRows = 0;
  for (let i = 1; i < lines.length; i++) {
    if (lines[i].trim()) totalRows++;
  }

  // Parse data rows (limited to MAX_ROWS)
  const rows: CsvRow[] = [];
  for (let i = 1; i < lines.length && rows.length < MAX_ROWS; i++) {
    const line = lines[i];
    // Skip empty lines
    if (!line.trim()) continue;

    const values = parseCSVLine(line, delimiter);
    const row: CsvRow = {};

    columnIds.forEach((columnId, index) => {
      row[columnId] = values[index]?.trim() ?? '';
    });

    rows.push(row);
  }

  return { headers, columnIds, rows, totalRows, truncated: totalRows > MAX_ROWS };
}

/**
 * Parse a single CSV line, handling quoted values correctly
 */
function parseCSVLine(line: string, delimiter: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];
    const nextChar = line[i + 1];

    if (inQuotes) {
      if (char === '"') {
        if (nextChar === '"') {
          // Escaped quote
          current += '"';
          i++; // Skip next quote
        } else {
          // End of quoted value
          inQuotes = false;
        }
      } else {
        current += char;
      }
    } else {
      if (char === '"') {
        // Start of quoted value
        inQuotes = true;
      } else if (char === delimiter) {
        // End of field
        result.push(current);
        current = '';
      } else {
        current += char;
      }
    }
  }

  // Push last field
  result.push(current);

  return result;
}

// ============================================
// Column Header with Sort and Filter
// ============================================

interface ColumnHeaderProps {
  column: {
    getIsSorted: () => false | 'asc' | 'desc';
    toggleSorting: (desc?: boolean) => void;
    getFilterValue: () => unknown;
    setFilterValue: (value: unknown) => void;
    getCanSort: () => boolean;
  };
  header: string;
  showFilter: boolean;
  onFilterToggle: () => void;
}

function ColumnHeader({ column, header, showFilter, onFilterToggle }: ColumnHeaderProps) {
  const sortDirection = column.getIsSorted();
  const filterValue = (column.getFilterValue() as string) ?? '';

  return (
    <div className="flex flex-col gap-1">
      <div className="flex items-center gap-1">
        <button
          className={cn(
            'flex items-center gap-1 px-1 py-0.5 -mx-1 rounded text-left',
            'hover:bg-surface-200 dark:hover:bg-surface-700',
            'transition-colors text-xs font-semibold',
            'text-surface-700 dark:text-surface-300'
          )}
          onClick={() => column.toggleSorting()}
        >
          <span className="truncate max-w-32">{header}</span>
          {sortDirection === 'asc' && <ArrowUp className="h-3 w-3 shrink-0 text-primary-600" />}
          {sortDirection === 'desc' && <ArrowDown className="h-3 w-3 shrink-0 text-primary-600" />}
          {!sortDirection && <ArrowUpDown className="h-3 w-3 shrink-0 opacity-40" />}
        </button>
        <button
          className={cn(
            'p-0.5 rounded hover:bg-surface-200 dark:hover:bg-surface-700',
            'transition-colors',
            (showFilter || filterValue) && 'bg-primary-100 dark:bg-primary-900/30 text-primary-600'
          )}
          onClick={onFilterToggle}
          title={showFilter ? 'Hide filter' : 'Show filter'}
        >
          <Filter className="h-3 w-3" />
        </button>
      </div>
      {showFilter && (
        <div className="flex items-center gap-1">
          <Input
            type="text"
            value={filterValue}
            onChange={(e) => column.setFilterValue(e.target.value || undefined)}
            placeholder="Filter..."
            className="h-6 text-xs px-2 py-0"
          />
          {filterValue && (
            <button
              className="p-0.5 rounded hover:bg-surface-200 dark:hover:bg-surface-700"
              onClick={() => column.setFilterValue(undefined)}
              title="Clear filter"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ============================================
// CsvViewer Component
// ============================================

export function CsvViewer({ content, filename, className }: CsvViewerProps) {
  const [sorting, setSorting] = useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([]);
  const [visibleFilters, setVisibleFilters] = useState<Set<string>>(new Set());

  // Detect delimiter and parse CSV
  const { headers, columnIds, rows, totalRows, truncated, delimiter } = useMemo(() => {
    const detected = detectDelimiter(content);
    const parsed = parseCsv(content, detected);
    return { ...parsed, delimiter: detected };
  }, [content]);

  // Toggle filter visibility for a column
  const toggleFilter = useCallback((columnId: string) => {
    setVisibleFilters((prev) => {
      const next = new Set(prev);
      if (next.has(columnId)) {
        next.delete(columnId);
      } else {
        next.add(columnId);
      }
      return next;
    });
  }, []);

  // Build columns dynamically from headers
  const columns = useMemo<ColumnDef<CsvRow>[]>(() => {
    return columnIds.map((columnId, index) => ({
      accessorKey: columnId,
      header: ({ column }) => (
        <ColumnHeader
          column={column}
          header={headers[index]}
          showFilter={visibleFilters.has(columnId)}
          onFilterToggle={() => toggleFilter(columnId)}
        />
      ),
      cell: ({ getValue }) => {
        const value = getValue() as string;
        // Handle NULL indicators: \N (MySQL), NULL, null, empty
        const isNull = !value || value === '\\N' || value.toLowerCase() === 'null';
        return (
          <span
            className={`block truncate max-w-xs ${isNull ? 'text-surface-400 dark:text-surface-600 italic' : ''}`}
            title={isNull ? 'NULL' : value}
          >
            {isNull ? '—' : value}
          </span>
        );
      },
      filterFn: 'includesString',
    }));
  }, [columnIds, headers, visibleFilters, toggleFilter]);

  // Create table instance with pagination
  const table = useReactTable({
    data: rows,
    columns,
    state: {
      sorting,
      columnFilters,
    },
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    initialState: {
      pagination: {
        pageSize: 100,
      },
    },
  });

  // Get display name for delimiter
  const delimiterName = useMemo(() => {
    switch (delimiter) {
      case ',': return 'comma';
      case '\t': return 'tab';
      case '|': return 'pipe';
      case ';': return 'semicolon';
      default: return delimiter;
    }
  }, [delimiter]);

  // Empty state
  if (headers.length === 0 || columnIds.length === 0) {
    return (
      <div className={cn('h-full flex items-center justify-center p-8', className)}>
        <div className="text-center">
          <FileSpreadsheet className="h-12 w-12 text-surface-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-surface-700 dark:text-surface-300 mb-1">
            Empty CSV File
          </h3>
          <p className="text-sm text-surface-500">{filename}</p>
        </div>
      </div>
    );
  }

  const filteredRowCount = table.getFilteredRowModel().rows.length;
  const loadedRowCount = rows.length;
  const hasActiveFilters = columnFilters.length > 0;

  return (
    <div className={cn('h-full flex flex-col', className)}>
      {/* Truncation warning */}
      {truncated && (
        <div className="flex items-center gap-2 px-4 py-2 bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-300 text-xs">
          <span>
            Showing first <span className="font-medium">{loadedRowCount.toLocaleString()}</span> of{' '}
            <span className="font-medium">{totalRows.toLocaleString()}</span> rows.
            Large files are limited for performance.
          </span>
        </div>
      )}

      {/* Stats bar */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-surface-200 dark:border-surface-800 bg-white dark:bg-surface-900 shrink-0">
        <div className="flex items-center gap-4 text-xs text-surface-600 dark:text-surface-400">
          <span>
            <span className="font-medium">{headers.length}</span> columns
          </span>
          <span>
            {hasActiveFilters ? (
              <>
                <span className="font-medium">{filteredRowCount.toLocaleString()}</span> of{' '}
                <span className="font-medium">{loadedRowCount.toLocaleString()}</span> rows
              </>
            ) : (
              <>
                <span className="font-medium">{loadedRowCount.toLocaleString()}</span> rows
                {truncated && <span className="text-surface-400"> (of {totalRows.toLocaleString()})</span>}
              </>
            )}
          </span>
          <span className="text-surface-400">Delimiter: {delimiterName}</span>
        </div>
        {hasActiveFilters && (
          <button
            className="text-xs text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
            onClick={() => setColumnFilters([])}
          >
            Clear all filters
          </button>
        )}
      </div>

      {/* Table container */}
      <div className="flex-1 overflow-auto">
        <table className="w-full border-collapse min-w-max">
          <thead className="sticky top-0 z-10 bg-surface-100 dark:bg-surface-800">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id}>
                {/* Row number header */}
                <th
                  className={cn(
                    'sticky left-0 z-20',
                    'px-3 py-2 text-left text-xs font-semibold',
                    'text-surface-500 dark:text-surface-500',
                    'bg-surface-100 dark:bg-surface-800',
                    'border-b border-r border-surface-200 dark:border-surface-700',
                    'w-12 min-w-12'
                  )}
                >
                  #
                </th>
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className={cn(
                      'px-3 py-2 text-left',
                      'border-b border-r border-surface-200 dark:border-surface-700',
                      'bg-surface-100 dark:bg-surface-800',
                      'min-w-24'
                    )}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(header.column.columnDef.header, header.getContext())}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map((row, rowIndex) => {
              // Calculate actual row number accounting for pagination
              const pageIndex = table.getState().pagination.pageIndex;
              const pageSize = table.getState().pagination.pageSize;
              const actualRowNumber = pageIndex * pageSize + rowIndex + 1;

              return (
                <tr
                  key={row.id}
                  className={cn(
                    'hover:bg-surface-50 dark:hover:bg-surface-900/50',
                    'transition-colors'
                  )}
                >
                  {/* Row number cell */}
                  <td
                    className={cn(
                      'sticky left-0 z-10',
                      'px-3 py-1.5 text-xs text-right',
                      'text-surface-400 dark:text-surface-500',
                      'bg-surface-50 dark:bg-surface-950',
                      'border-b border-r border-surface-200 dark:border-surface-800',
                      'font-mono'
                    )}
                  >
                    {actualRowNumber}
                  </td>
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className={cn(
                        'px-3 py-1.5 text-sm',
                        'text-surface-800 dark:text-surface-200',
                        'border-b border-r border-surface-200 dark:border-surface-800'
                      )}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* No results message */}
        {filteredRowCount === 0 && hasActiveFilters && (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <Filter className="h-8 w-8 text-surface-400 mb-3" />
            <p className="text-surface-600 dark:text-surface-400 mb-2">
              No rows match your filters
            </p>
            <button
              className="text-sm text-primary-600 hover:text-primary-700 dark:text-primary-400"
              onClick={() => setColumnFilters([])}
            >
              Clear all filters
            </button>
          </div>
        )}
      </div>

      {/* Pagination controls */}
      <div className="flex items-center justify-between px-4 py-2 border-t border-surface-200 dark:border-surface-800 bg-white dark:bg-surface-900 shrink-0">
        <div className="flex items-center gap-2">
          <span className="text-xs text-surface-600 dark:text-surface-400">Rows per page:</span>
          <select
            value={table.getState().pagination.pageSize}
            onChange={(e) => table.setPageSize(Number(e.target.value))}
            className={cn(
              'h-7 px-2 text-xs rounded border',
              'bg-white dark:bg-surface-800',
              'border-surface-300 dark:border-surface-600',
              'text-surface-700 dark:text-surface-300',
              'focus:outline-none focus:ring-1 focus:ring-primary-500'
            )}
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-1 text-xs text-surface-600 dark:text-surface-400">
          <span>
            Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
          </span>
        </div>

        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            size="icon"
            className="h-7 w-7"
            onClick={() => table.setPageIndex(0)}
            disabled={!table.getCanPreviousPage()}
            title="First page"
          >
            <ChevronsLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-7 w-7"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            title="Previous page"
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-7 w-7"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            title="Next page"
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="icon"
            className="h-7 w-7"
            onClick={() => table.setPageIndex(table.getPageCount() - 1)}
            disabled={!table.getCanNextPage()}
            title="Last page"
          >
            <ChevronsRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}

export default CsvViewer;
